import { DateTime } from 'luxon';

import { Button } from '@/components/Button';
import { Sheet } from '@/components/Sheet';
import { formatDuration } from '@/features/tasks/tree';
import type { EstimateDelta, FreeSlot } from './estimateChoice';

interface Props {
  taskTitle: string;
  estimateMinutes: number;
  delta: EstimateDelta;
  /** Where the leftover would go, when there is room for it. */
  remainderSlot: FreeSlot | null;
  timezone: string;
  onClose: () => void;
  onUpdateEstimate: () => void;
  onScheduleRemainder: () => void;
}

const duration = (minutes: number) => formatDuration(minutes) ?? `${minutes}m`;

/**
 * Asked after a resize changes how much time a task has on the calendar.
 *
 * Two different intentions produce the same gesture: "this is smaller than I
 * thought" (the estimate was wrong) and "this is all the time I have today"
 * (the estimate is right, the rest needs a slot). Guessing wrong quietly
 * corrupts either the estimate or the plan, so the question gets asked once,
 * here, with both answers spelled out.
 */
export function EstimateChoiceSheet({
  taskTitle,
  estimateMinutes,
  delta,
  remainderSlot,
  timezone,
  onClose,
  onUpdateEstimate,
  onScheduleRemainder,
}: Props) {
  const under = delta.outcome === 'under';
  const slotLabel = remainderSlot
    ? `${DateTime.fromJSDate(remainderSlot.startsAt).setZone(timezone).toFormat('ccc, LLL d · h:mm a')} – ${DateTime.fromJSDate(remainderSlot.endsAt).setZone(timezone).toFormat('h:mm a')}`
    : null;

  return (
    <Sheet
      title={under ? 'Less time than the estimate' : 'More time than the estimate'}
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Leave both
          </Button>
          {under && remainderSlot && (
            <Button variant="secondary" onClick={onScheduleRemainder}>
              Schedule the rest
            </Button>
          )}
          <Button onClick={onUpdateEstimate}>
            {under ? 'Shorten estimate' : 'Raise estimate'}
          </Button>
        </div>
      }
    >
      <p className="text-body text-text">
        <span className="font-medium">{taskTitle}</span> is estimated at{' '}
        {duration(estimateMinutes)} and now has {duration(delta.plannedMinutes)} on the
        calendar.
      </p>

      <ul className="mt-4 space-y-3 text-small text-text-secondary">
        <li>
          <span className="font-medium text-text">
            {under ? 'Shorten estimate' : 'Raise estimate'}
          </span>
          {': the estimate becomes '}
          {duration(delta.plannedMinutes)}. Use this when the
          resize was you correcting how long the work actually takes.
        </li>

        {under &&
          (remainderSlot ? (
            <li>
              <span className="font-medium text-text">Schedule the rest</span>
              {': keep the '}
              {duration(estimateMinutes)} estimate and place the remaining{' '}
              {duration(delta.deltaMinutes)} in the next free slot,{' '}
              <span className="tabular text-text">{slotLabel}</span>. It stays one task,
              continued.
            </li>
          ) : (
            <li>
              <span className="font-medium text-text">Schedule the rest</span>
              {': unavailable; nothing free in the next week is long enough for the remaining '}
              {duration(delta.deltaMinutes)}.
            </li>
          ))}

        <li>
          <span className="font-medium text-text">Leave both</span>
          {': the block keeps its new length and the estimate stays at '}
          {duration(estimateMinutes)}
          {under ? `, with ${duration(delta.deltaMinutes)} still unplanned.` : '.'}
        </li>
      </ul>
    </Sheet>
  );
}
